import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data-loading", "failed", "grow", "label", "labelCompletedText", "layout", "minHeight", "onResultDisplayFinish", "resultAnimationDuration", "resultIcon", "role", "showLabel", "showResult", "size", "use", "hidden"];
import { useEffect, useRef } from 'react';
import { keyframes } from 'styled-components';
import styled, { isTrellisTheme, useTheme } from 'foundations-theming/utils';
import { toShorthandSize } from '../../utils/tShirtSizes';
import emptyFunction from 'react-utils/emptyFunction';
import { filterProps } from '../../../utils/filterProps';
import { IconHolder } from '../IconHolder';
import { getComputedUse, getIconColor, getIconSize, getSpinnerSize } from './LoadingSpinner.helpers';
import { useIndicatorColor, useRingColor } from './LoadingSpinner.hooks';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { lazyEval } from '../../utils/lazyEval';
import unescapedText from 'I18n/utils/unescapedText';
import { useIsInFilledToolbar } from '../ToolBar/ToolBarContext';
import { SuccessIcon } from 'foundations-assets/icons/components/SuccessIcon';
import { useLoadingButtonContext } from '../LoadingButton/LoadingButtonProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const privateSpin = keyframes(["100%{transform:rotate(360deg)}"]);
const privateDash = keyframes(["0%{stroke-dasharray:1,150;stroke-dashoffset:0;}50%{stroke-dasharray:90,150;stroke-dashoffset:-50;}100%{stroke-dasharray:90,150;stroke-dashoffset:-140;}"]);
export const SpinnerOuter = styled.div.withConfig({
  displayName: "LoadingSpinner__SpinnerOuter"
})(({
  $grow,
  $use,
  $layout,
  $minHeight,
  $cssStyles,
  theme
}) => {
  return Object.assign({
    position: 'relative',
    alignItems: 'center',
    color: $use === 'on-dark' ? theme.color['fill-inverse-default-alt'] : 'currentColor',
    display: 'flex'
  }, $layout === 'centered' && {
    flexDirection: 'column',
    justifyContent: 'center'
  }, $grow ? {
    inlineSize: '100%',
    blockSize: '100%'
  } : {
    margin: theme.space['200']
  }, {
    minBlockSize: typeof $minHeight === 'number' ? `${$minHeight}px` : $minHeight
  }, $minHeight && !$grow && {
    blockSize: '16px'
  }, $cssStyles);
});
const SpinnerInner = styled.div.withConfig({
  displayName: "LoadingSpinner__SpinnerInner"
})(({
  $size
}) => {
  const spinnerSize = getSpinnerSize($size);
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    blockSize: spinnerSize,
    inlineSize: spinnerSize
  };
});
const SpinnerLabel = styled.span.withConfig({
  displayName: "LoadingSpinner__SpinnerLabel"
})(({
  $layout,
  theme
}) => {
  const BASE_ICON_SPACING_X = theme.space['100'];
  return {
    marginBlockStart: $layout === 'centered' ? BASE_ICON_SPACING_X : 0,
    '&:not(:empty)': {
      marginInlineStart: BASE_ICON_SPACING_X,
      marginInlineEnd: BASE_ICON_SPACING_X
    }
  };
});
const ResultSpinner = styled.div.withConfig({
  displayName: "LoadingSpinner__ResultSpinner"
})(({
  $showResult
}) => {
  return Object.assign({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    position: 'absolute',
    transform: 'scale(0.7)',
    transition: `opacity 0.2s cubic-bezier(0.42, 0, 0.58, 1) 0.1s, transform 0.3s cubic-bezier(0.2, 0.9, 0.3, 2) 0.1s`
  }, $showResult && {
    opacity: 1,
    position: 'relative',
    transform: 'scale(1)'
  });
});
const SpinnerWrapper = styled.div.withConfig({
  displayName: "LoadingSpinner__SpinnerWrapper"
})([".hs-private.hs-private.hs-private &{opacity:1;transition:opacity 0.2s cubic-bezier(0.42,0,0.58,1),transform 0.2s cubic-bezier(0.89,0.03,0.68,0.22);", "}"], ({
  $showResult
}) => $showResult && `margin-left: -1px;
    opacity: 0;
    transform: scale(.7);
    width: 1px;
  `);
const Ring = styled.svg.withConfig({
  displayName: "LoadingSpinner__Ring"
})([".hs-private.hs-private.hs-private &{display:block;max-width:100%;}"]);
const RingPath = styled.circle.withConfig({
  displayName: "LoadingSpinner__RingPath"
})([".hs-private.hs-private.hs-private &{animation:", " 2s cubic-bezier(0.42,0,0.58,1) infinite,", " 2s linear infinite;stroke-dasharray:none;stroke-dashoffset:0;stroke-linecap:round;transform-origin:center;}"], privateDash, privateSpin);
export const LoadingSpinner = _ref => {
  var _loadingButtonContext;
  let {
      'data-loading': dataLoading,
      failed = false,
      grow = false,
      label = () => unescapedText('ui.UILoadingSpinner.busyText'),
      labelCompletedText = () => unescapedText('ui.UILoadingSpinner.completedText'),
      layout,
      minHeight,
      onResultDisplayFinish = emptyFunction,
      resultAnimationDuration = 1500,
      resultIcon: ResultIcon = SuccessIcon,
      role = 'status',
      showLabel = false,
      showResult = false,
      size = 'sm',
      use = 'link',
      hidden
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(Object.assign({}, rest, {
    role
  }));
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const theme = useTheme();
  const isTrellis = isTrellisTheme(theme);
  const isInFilledToolbar = useIsInFilledToolbar();
  const loadingButtonContext = useLoadingButtonContext();
  const isInLoadingButton = (_loadingButtonContext = loadingButtonContext === null || loadingButtonContext === void 0 ? void 0 : loadingButtonContext.isInLoadingButton) !== null && _loadingButtonContext !== void 0 ? _loadingButtonContext : false;
  const computedUse = getComputedUse({
    use,
    isInFilledToolbar,
    isTrellis,
    isInLoadingButton
  });
  const resultDisplayTimeoutRef = useRef(undefined);
  useEffect(() => {
    if (!showResult) return undefined;
    resultDisplayTimeoutRef.current = setTimeout(onResultDisplayFinish, resultAnimationDuration);
    return () => {
      clearTimeout(resultDisplayTimeoutRef.current);
    };
  }, [onResultDisplayFinish, resultAnimationDuration, showResult]);
  const computedLayout = layout || (grow ? 'centered' : 'inline');
  const computedLabelText = showResult && !failed ? lazyEval(labelCompletedText) : lazyEval(label);
  const shorthandSize = toShorthandSize(size);
  const spinnerSizePx = parseInt(getSpinnerSize(shorthandSize), 10);
  return /*#__PURE__*/_jsxs(SpinnerOuter, Object.assign({
    "data-loading": dataLoading !== undefined ? dataLoading : !showResult,
    hidden: hidden,
    $layout: computedLayout,
    $grow: grow,
    $use: computedUse,
    $cssStyles: cssStyles,
    $minHeight: minHeight
  }, domProps, {
    children: [/*#__PURE__*/_jsxs(SpinnerInner, {
      $size: shorthandSize,
      children: [/*#__PURE__*/_jsx(SpinnerWrapper, {
        $showResult: showResult,
        children: /*#__PURE__*/_jsxs(Ring, {
          height: spinnerSizePx,
          width: spinnerSizePx,
          viewBox: "0 0 50 50",
          stroke: useRingColor(computedUse),
          children: [/*#__PURE__*/_jsx("circle", {
            cx: "25",
            cy: "25",
            r: "22.5",
            fill: "none",
            strokeWidth: "5"
          }), /*#__PURE__*/_jsx(RingPath, {
            cx: "25",
            cy: "25",
            r: "22.5",
            fill: "none",
            stroke: useIndicatorColor(computedUse),
            strokeWidth: "5"
          })]
        })
      }), ResultIcon && !failed && /*#__PURE__*/_jsx(ResultSpinner, {
        $showResult: showResult,
        children: /*#__PURE__*/_jsx(IconHolder, {
          icon: ResultIcon,
          color: getIconColor({
            use: computedUse
          }),
          size: getIconSize(shorthandSize)
        })
      })]
    }), showLabel ? /*#__PURE__*/_jsx(SpinnerLabel, {
      $layout: computedLayout,
      children: computedLabelText
    }) : /*#__PURE__*/_jsx(ScreenReaderOnly, {
      children: computedLabelText
    })]
  }));
};
LoadingSpinner.displayName = 'LoadingSpinner';